import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import type { Performance, Video } from "../api/types";
import { useServices } from "../app/services";
import { useResource } from "../app/useResource";
import { PerformanceCard } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, PageHeader, ResourceView, Thumb } from "../components/ui";
import { formatMs } from "../lib/time";
import { looksLikeYouTubeUrl, watchUrl } from "../lib/youtube";
import { SearchForm } from "./DiscoverPage";

/**
 * 検索：登録済みの歌唱と、まだ登録されていない動画の候補を分けて出す（仕様書 /catalog/search）。
 * 登録済み歌唱はキューに追加できる。動画の再生・新規歌唱登録はDiscordから行う。
 */
export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const { api } = useServices();
  const [page, setPage] = useState(1);
  const [more, setMore] = useState<Performance[]>([]);
  const [hasMore, setHasMore] = useState<boolean | null>(null);
  useEffect(() => {
    setPage(1);
    setMore([]);
    setHasMore(null);
  }, [q]);
  const res = useResource("catalog.search", () => (q ? api.search(q, 1) : Promise.resolve(null)), [q], ["library"]);
  const [moreError, setMoreError] = useState(false);
  const loadMore = async () => {
    setMoreError(false);
    try {
      const next = await api.search(q, page + 1);
      setMore((m) => [...m, ...next.performances.items]);
      setHasMore(next.performances.has_more);
      setPage(page + 1);
    } catch {
      setMoreError(true);
    }
  };
  const isUrl = looksLikeYouTubeUrl(q);

  return (
    <div className="page">
      <PageHeader title="探す" lead="曲名・別名・歌った人で探せます。新しい動画を流すときはDiscordの「曲を追加」へ。">
        <SearchForm key={q} initial={q} />
      </PageHeader>
      {isUrl && (
        <p className="notice notice--info">
          <Icon name="link" className="notice__icon" />
          <span>
            このURLをDiscordの「曲を追加」に貼り付けると再生できます。
          </span>
        </p>
      )}
      {!q ? (
        <EmptyState title="探したい曲名や歌った人を入れてください" icon="search" />
      ) : (
        <ResourceView resource={res} what="検索">
          {(d) =>
            d && (
              <>
                <section className="section" aria-labelledby="sec-registered">
                  <h2 id="sec-registered" className="section-title">
                    登録済みの歌唱{" "}
                    <span className="count">
                      {d.performances.items.length + more.length}
                      {d.performances.has_more ? "+" : ""}
                    </span>
                  </h2>
                  {d.performances.items.length > 0 ? (
                    <>
                      <ul className="perf-list">
                        {[...d.performances.items, ...more].map((p) => (
                          <PerformanceCard key={p.id} performance={p} />
                        ))}
                      </ul>
                      {(hasMore ?? d.performances.has_more) && (
                        <p className="section-more">
                          <button type="button" className="btn btn-secondary" onClick={loadMore}>
                            もっと見る
                          </button>
                          {moreError && <span className="inline-note inline-note--danger">読み込めませんでした。</span>}
                        </p>
                      )}
                    </>
                  ) : (
                    <EmptyState title={`「${q}」に合う登録済みの歌唱はありません`} icon="search" />
                  )}
                </section>
                <section className="section" aria-labelledby="sec-videos">
                  <h2 id="sec-videos" className="section-title">
                    YouTubeの動画 <span className="count">{d.videos.length}</span>
                  </h2>
                  <p className="section-basis">
                    動画のURLをDiscordの「曲を追加」に貼り付けて再生できます。聴きながら「ここ好き」も残せます。
                  </p>
                  {d.videos.length > 0 ? (
                    <ul className="video-list">
                      {d.videos.map((v) => (
                        <VideoCandidate key={v.video_id} video={v} />
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">候補はありません。</p>
                  )}
                </section>
              </>
            )
          }
        </ResourceView>
      )}
    </div>
  );
}

function VideoCandidate({ video }: { video: Video }) {
  return (
    <li className="video-card">
      <Thumb videoId={video.video_id} title={video.title} size="s" />
      <div className="video-card__body">
        <p className="video-card__title">{video.title}</p>
        <p className="video-card__sub">動画の長さ {formatMs(video.duration_ms)}</p>
      </div>
      <a className="btn btn-secondary btn-small" href={watchUrl(video.video_id)} target="_blank" rel="noreferrer">
        YouTubeで開く
        <span className="visually-hidden">：{video.title}</span>
      </a>
    </li>
  );
}
